// Pure parse/save model for MetadataForm (2026-07 DI UI audit, finding F15).
//
// The old form was written against an imagined metadata.json: it edited a
// phantom `fiscal_year.start_month` (defaulting to January — wrong for a
// September-fiscal-year customer) beside the real `end_month`, injected
// `description` / `industry` / `date_conventions.fiscal_year_naming` /
// `rolling_lookback_months` that nothing consumes, and read `datasets` as an
// array when production stores an object keyed by role with `inboxID`.
//
// This model edits ONLY fields that exist in the production contract and
// preserves everything else verbatim (merge semantics from shared/formMerge).

import { specs, mergeRow, mergeSection, rowFromSpecs, setOrDelete, clone } from '../shared/formMerge'

const TOP_FIELDS = {
    workspace_name: specs.text,
}

const DATA_COVERAGE_FIELDS = {
    earliest_date: specs.text,
    latest_date: specs.text,
    granularity: specs.text,
    note: specs.text,
}

// Production truth: the fiscal year is expressed as the month it ENDS
// (end_month, e.g. 9 for a September-ending year). There is no start_month.
const FISCAL_YEAR_FIELDS = {
    end_month: specs.number,
    end_month_name: specs.text,
    quarterly_naming: specs.text,
    note: specs.text,
}

// date_conventions holds balance/flow metric query semantics — not display
// preferences.
const BALANCE_METRIC_FIELDS = {
    description: specs.text,
    query_date: specs.text,
    posting_convention: specs.text,
}

const FLOW_METRIC_FIELDS = {
    description: specs.text,
    query_range: specs.text,
}

// Advanced sections that shape every generated report. Surfaced read-only in
// the form (full editors are follow-up work); listed here so the UI can say
// they exist instead of hiding them (audit F15 "missing surfaces").
export const ADVANCED_SECTIONS = [
    ['rollup_guardrails', 'Rollup Guardrails', 'Injected into every rollup and entity synthesis prompt'],
    ['terminology', 'Terminology', 'Customer-facing labels and replacements used in reports'],
    ['action_planning', 'Action Planning', 'Catalogs, guardrails, QA checklist, recommendation defaults'],
    ['organizational_hierarchy', 'Organizational Hierarchy', 'Levels, divisions, and counts'],
    ['entity_mapping', 'Entity Mapping', 'Runtime entity resolution for batch execution'],
    ['ai_billing', 'AI Billing', 'Provider routing and chargeback (read-only)'],
]

export const parseMetadataToForm = (data) => ({
    ...rowFromSpecs(data, TOP_FIELDS),
    data_coverage: rowFromSpecs(data?.data_coverage, DATA_COVERAGE_FIELDS),
    fiscal_year: rowFromSpecs(data?.fiscal_year, FISCAL_YEAR_FIELDS),
    balance_metrics: rowFromSpecs(data?.date_conventions?.balance_metrics, BALANCE_METRIC_FIELDS),
    flow_metrics: rowFromSpecs(data?.date_conventions?.flow_metrics, FLOW_METRIC_FIELDS),
})

export const buildMetadataFromForm = (data, formData) => {
    const out = mergeRow(data, formData, TOP_FIELDS)

    setOrDelete(
        out,
        'data_coverage',
        mergeSection(data?.data_coverage, formData.data_coverage, DATA_COVERAGE_FIELDS)
    )
    setOrDelete(
        out,
        'fiscal_year',
        mergeSection(data?.fiscal_year, formData.fiscal_year, FISCAL_YEAR_FIELDS)
    )

    const dateConventions = data?.date_conventions ? clone(data.date_conventions) : {}
    setOrDelete(
        dateConventions,
        'balance_metrics',
        mergeSection(data?.date_conventions?.balance_metrics, formData.balance_metrics, BALANCE_METRIC_FIELDS)
    )
    setOrDelete(
        dateConventions,
        'flow_metrics',
        mergeSection(data?.date_conventions?.flow_metrics, formData.flow_metrics, FLOW_METRIC_FIELDS)
    )
    setOrDelete(
        out,
        'date_conventions',
        Object.keys(dateConventions).length > 0 ? dateConventions : undefined
    )

    return out
}

/**
 * Normalize `datasets` for display. Production stores an OBJECT keyed by role
 * (primary / inventory / full_history) with `inboxID`; older workspaces may
 * hold an array. Returns [{key, name, inboxID, note}] either way.
 */
export const listDatasets = (datasets) => {
    if (!datasets) return []
    if (Array.isArray(datasets)) {
        return datasets.map((d, i) => ({
            key: d.name || d.inbox_id || d.dataset_id || `dataset-${i}`,
            name: d.name || d.inbox_id || d.dataset_id || `Dataset ${i + 1}`,
            inboxID: d.inboxID || d.inbox_id || d.dataset_id || null,
            note: d.description || d.note || null,
        }))
    }
    if (typeof datasets === 'object') {
        return Object.entries(datasets).map(([key, d]) => ({
            key,
            name: d?.name || key,
            inboxID: d?.inboxID || d?.inbox_id || null,
            note: d?.note || d?.description || null,
        }))
    }
    return []
}
